import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from "react-markdown";
import {getBalls, getScore, roundTo3} from "../common/CalculateScores";
import AlertDialogNote from "../common/DialogNote";

// ---------- Стили ----------
const containerStyle = {
    display: 'flex',
    //flexWrap: 'wrap',          // если ширина окна маленькая – переносится на новую строку
    gap: '0px',                // расстояние между блоками
};

const dotBaseStyle = {
    width: '40px',
    height: '40px',
    lineHeight: '40px',
    textAlign: 'center',
    fontSize: '16px',
    borderRadius: '0px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color .2s, color .2s',
};

const nameStyle = {
    width: '40px',
    height: '40px',
    lineHeight: '40px',
    textAlign: 'center',
    fontSize: '18px',
    borderRadius: '0px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color .2s, color .2s',
   //  borderBottom: "1px outset #fff"
};

const selectedStyle = {
    backgroundColor: '#ffa500', // зелёный
    color: '#fff',
    userSelect: 'none',
    //borderBottom: "1px outset #fff"
};

const defaultStyle = {
    backgroundColor: '#6e6c6c', // зелёный
    userSelect: 'none',
    //borderBottom: "1px outset #fff"
};

const disableStyle = {
    backgroundColor: '#b2b2b2', // зелёный
    userSelect: 'none',
   // borderBottom: "1px outset #fff"
};


const bonusDefaultStyle = {
    backgroundColor: '#6e6c6c', // зелёный
    userSelect: 'none',
    borderRight: "2px solid #ff6200",
    borderTop: "2px solid #ff6200",
    borderBottom: "2px solid #ff6200",
};
const bonusSelectedStyle = {
    backgroundColor: '#ff6200', // зелёный
    color: '#fff',
    userSelect: 'none',
    border: "2px solid #ff6200"
};


const bonusDisableStyle = {
    backgroundColor: '#b2b2b2', // зелёный
    userSelect: 'none',
    // borderBottom: "1px outset #fff"
};

// ---------- Компонент ----------
function ScoreDots({ onSelect, name, max, disable=false, bonus=false, value=null }) {
    const [selected, setSelected] = useState(value ?? 0);

    useEffect(() => {
        // Синхронизируем с внешним значением при его изменении
        if (value !== undefined && value !== null) {
            setSelected(value);
        }
    }, [value]);

    // Создаём массив значений от 0 до 5 с шагом 0.5
    const values = [];
    for (let v = 0; v <= max; v += 0.5) {
        values.push(v);
    }

    const handleClick = value => {
        if(!disable){
            setSelected(value);     // локальное состояние – чтобы показать цвет
            onSelect && onSelect(value); // передаём наружу
        }
    };

    return (
        <div style={containerStyle}>
            {name ? <div style={{...nameStyle}}>
                <AlertDialogNote
                    ok={"Понятно"}
                    okFn={()=>{}}
                    cancel=""
                    cancelFn={
                        ()=>{}
                    }
                    message={
                        <>
                            <ReactMarkdown>
                                {
`
# Руководство для судей
## Основной ход Boogie Woogie  

### Партнер (**B**asics **L**eader)

\`\`\`
BL  0 - 1 - 2 - 3 - 4 - 5
\`\`\`

### Партнерша (**B**asics **F**ollower)

\`\`\`
BW  0 - 1 - 2 - 3 - 4 - 5
\`\`\`


### 0–5 Ритм / Работа ног

* **0–2** низкий уровень (три повторяющиеся ошибки)
* **3** средне-низкий уровень (две повторяющиеся ошибки)
* **4** средний уровень (одна повторяющаяся ошибка)
* **4,5** хорошо (ошибки иногда)
* **5** высокий уровень

---
 
## Ведение и следование, базовое танцевание, гармония, сценическое исполнение

**15 баллов**

\`\`\`
LF 0 - 1 - 2 - 3 - 4 - 5
DP 0 - 1 - 2 - 3 - 4
BONUS 0 - 1
\`\`\`

### 0–5 Ведение и следование (Lead & Follow), базовое танцевание, гармония

* **0–2** низкий уровень (три повторяющиеся ошибки)
* **3** средне-низкий уровень (две повторяющиеся ошибки)
* **4** средний уровень (одна повторяющаяся ошибка)
* **4,5** хорошо (ошибки иногда)
* **5** высокий уровень

### 0–4 Танцевальное исполнение (Dance Performance)

* **0–1** низкий уровень
* **2** средне-низкий уровень
* **3** средний уровень
* **3,5** хорошо
* **4** высокий уровень

> *Общее впечатление является решающим*

### 0–1 Бонус: базовые фигуры

* **0** без бонуса
* **0,5** хорошо
* **1** высокий уровень

---
 
## Танцевальные фигуры (как они подаются)

**10 баллов**

\`\`\`
EX 0 - 1 - 2 - 3 - 4 - 5
DV 0 - 1 - 2 - 3 - 4    
BONUS 0 - 1
\`\`\`

### 0–5 Исполнение (Execution)

* **0–2** низкий уровень
* **3** средне-низкий уровень
* **4** средний уровень
* **4,5** хорошо
* **5** высокий уровень

### 0–4 Сложность, разнообразие (Difficulty, Variety)

* **0–1** низкий уровень
* **2** средне-низкий уровень
* **3** средний уровень
* **3,5** хорошо
* **4** высокий уровень

### 0–1 Бонус

* **0** без бонуса
* **0,5** хорошо
* **1** высокий уровень

---
 
## Музыкальная интерпретация (что они представляют)

**25 баллов**

\`\`\`
IN 0 - 1 - 2 - 3 - 4 - 5
SP 0 - 1 - 2 - 3 - 4
BONUS 0 - 1
\`\`\`

### 0–5 Интерпретация с акцентами и вариации основного хода  (Interpretation with Highlights and advanced basic)

* **0–2** только на последний акцент (низкий уровень)
* **3–4** по музыкальной структуре (средний уровень)
* **4,5** связано с музыкой (хорошо)
* **5** по структуре и полностью связано с музыкой (высокий уровень)

### 0–4 Спонтанная интерпретация (Spontaneous Interpretation)

* **0,5–2** связано с музыкой — один танцор

  * 0–1 низкий уровень
  * 2 средний
* **3–4** связано с музыкой — оба танцора

  * 3,5 хорошо
  * 4 высокий уровень

### 0–1 Бонус

* **0** без бонуса
* **0,5** хорошо
* **1** высокий уровень
`}
                            </ReactMarkdown>
                        </>
                    }
                >{name}
                </AlertDialogNote>
            </div> :""}
            {values.map(val => (
                <div
                    key={val}
                    style={{
                        ...dotBaseStyle,
                        ...(val <= selected ? (disable ? disableStyle : (bonus ? bonusSelectedStyle: selectedStyle) ) : (bonus ? (disable ? bonusDisableStyle :bonusDefaultStyle) : defaultStyle)),
                    }}
                    onClick={() => handleClick(val)}
                >
                    {
                       Number.isInteger(val) ? val : "-"
                    }
                </div>
            ))}
        </div>
    );
}

ScoreDots.propTypes = {
    /** Функция, вызываемая при выборе значения. */
    onSelect: PropTypes.func,
    /** Текущее значение */
    value: PropTypes.number,
};

export default ScoreDots;